import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Plus, Calendar, Building2, User, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LayoutWrapper } from '@/components/LayoutWrapper';
import { PageHeader } from '@/components/PageHeader';
import { format } from 'date-fns';

interface Incident {
  id: string;
  title: string;
  description: string | null;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  due_date: string | null;
  resolved_at: string | null;
  created_at: string;
  created_by: string;
  assigned_to: string | null;
  unit_id: string;
  corrective_action: string | null;
  units: {
    name: string;
  };
  created_by_user?: {
    name: string;
  };
  assigned_user?: {
    name: string;
  };
}

interface Unit {
  id: string;
  name: string;
}

const Incidents = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'food_safety',
    severity: 'medium' as const,
    unit_id: '',
    due_date: ''
  });

  useEffect(() => {
    fetchIncidents();
    fetchUnits();
  }, [user]);

  const fetchIncidents = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('incidents')
        .select(`
          *,
          units!inner(name),
          created_by_user:users_public!incidents_created_by_fkey(name),
          assigned_user:users_public!incidents_assigned_to_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setIncidents(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading incidents",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('units')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setUnits(data || []);
    } catch (error: any) {
      console.error('Error loading units:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const createIncident = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setCreating(true);
    try {
      const { error } = await supabase
        .from('incidents')
        .insert({
          title: formData.title,
          description: formData.description || null,
          category: formData.category,
          severity: formData.severity,
          unit_id: formData.unit_id,
          due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
          created_by: user.id
        });

      if (error) throw error;

      toast({
        title: "Incident reported!",
        description: "The incident has been logged and assigned for follow-up.",
      });

      setDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        category: 'food_safety',
        severity: 'medium',
        unit_id: '',
        due_date: ''
      });
      fetchIncidents();
    } catch (error: any) {
      toast({
        title: "Error creating incident",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'high':
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'low':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'high':
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const filteredIncidents = incidents.filter(incident => {
    if (filter === 'resolved') return incident.resolved_at !== null;
    if (filter === 'open') return incident.resolved_at === null;
    return true;
  });

  const stats = {
    total: incidents.length,
    open: incidents.filter(i => !i.resolved_at).length,
    resolved: incidents.filter(i => i.resolved_at).length,
    overdue: incidents.filter(i => !i.resolved_at && i.due_date && new Date(i.due_date) < new Date()).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-accent/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading incidents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-accent/20">
      <PageHeader
        title="Incidents"
        description="Track and manage food safety incidents"
        backTo="/dashboard"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="cta-primary">
                <Plus className="h-4 w-4 mr-2" />
                Report Incident
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel max-w-md">
              <DialogHeader>
                <DialogTitle>Report New Incident</DialogTitle>
                <DialogDescription>
                  Document a food safety incident for tracking and corrective action.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createIncident}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Incident Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      placeholder="Temperature deviation in freezer unit"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="glass-input"
                      required
                      minLength={5}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder="Provide details about what happened..."
                      value={formData.description}
                      onChange={handleInputChange}
                      className="glass-input"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        className="w-full glass-input"
                      >
                        <option value="food_safety">Food Safety</option>
                        <option value="temperature">Temperature</option>
                        <option value="sanitation">Sanitation</option>
                        <option value="equipment">Equipment</option>
                        <option value="personnel">Personnel</option>
                        <option value="documentation">Documentation</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="severity">Severity</Label>
                      <select
                        id="severity"
                        name="severity"
                        value={formData.severity}
                        onChange={handleInputChange}
                        className="w-full glass-input"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="unit_id">Unit *</Label>
                    <select
                      id="unit_id"
                      name="unit_id"
                      value={formData.unit_id}
                      onChange={handleInputChange}
                      className="w-full glass-input bg-background/80 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:ring-1 focus:ring-primary/20 z-50"
                      required
                    >
                      <option value="" className="bg-background text-foreground">Select a restaurant</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id} className="bg-background text-foreground">
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date</Label>
                    <Input
                      id="due_date"
                      name="due_date"
                      type="date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      className="glass-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                    className="cta-secondary"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="cta-primary"
                    disabled={creating || !formData.title.trim() || !formData.unit_id}
                  >
                    {creating ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Reporting...
                      </div>
                    ) : (
                      'Report Incident'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('all')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-crystal-white">{stats.total}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('open')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-400">{stats.open}</div>
              <div className="text-xs text-muted-foreground">Open</div>
            </CardContent>
          </Card>
          <Card className="glass-panel cursor-pointer" onClick={() => setFilter('resolved')}>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{stats.resolved}</div>
              <div className="text-xs text-muted-foreground">Resolved</div>
            </CardContent>
          </Card>
          <Card className="glass-panel">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{stats.overdue}</div>
              <div className="text-xs text-muted-foreground">Overdue</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {(['all', 'open', 'resolved'] as const).map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? "cta-primary" : "cta-secondary"}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        {/* Incidents List */}
        {filteredIncidents.length === 0 ? (
          <div className="text-center py-16">
            <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-crystal-white mb-2">No incidents found</h2>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? "No incidents have been reported yet. This is good news!"
                : `No ${filter} incidents at the moment.`
              }
            </p>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="cta-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Report First Incident
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredIncidents.map((incident) => (
              <Card key={incident.id} className="glass-panel hover:glass-panel-elevated transition-all duration-200">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-2 rounded-lg bg-red-500/10">
                        {getSeverityIcon(incident.severity)}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{incident.title}</CardTitle>
                        <CardDescription className="flex items-center gap-4 mt-2 flex-wrap">
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {incident.units.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(incident.created_at), 'MMM d, yyyy')}
                          </div>
                          {incident.created_by_user && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {incident.created_by_user.name}
                            </div>
                          )}
                          {incident.due_date && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              Due: {format(new Date(incident.due_date), 'MMM d')}
                            </div>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${getSeverityColor(incident.severity)} capitalize`}>
                        {incident.severity}
                      </Badge>
                      {incident.resolved_at ? (
                        <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Resolved
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                          Open
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                {incident.description && (
                  <CardContent>
                    <p className="text-muted-foreground text-sm line-clamp-2">
                      {incident.description}
                    </p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Incidents;